import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { postLoginStatus } from '../utils/appNavigation';

function SimpleAuth({ onAuthenticated }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(() => {
    const raw = localStorage.getItem('authCooldownUntil');
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    let timer = null;
    const update = () => {
      if (!cooldownUntil) {
        setRemainingMs(0);
        setError(null);
        return;
      }
      const diff = cooldownUntil - Date.now();
      if (diff <= 0) {
        setRemainingMs(0);
        setCooldownUntil(0);
        setError(null);
        localStorage.removeItem('authCooldownUntil');
        if (timer) clearInterval(timer);
        return;
      }
      setRemainingMs(diff);
    };
    update();
    if (cooldownUntil) {
      timer = setInterval(update, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownUntil]);

  const formatMs = (ms) => {
    const total = Math.ceil(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmed = username.trim();

    if (!trimmed) return;
    if (!password) return;

    if (cooldownUntil && cooldownUntil > Date.now()) {
      // Still rate-limited
      return;
    }
    setIsLoading(true);

    try {
      const response = await fetchWithAuth(`auth/resolve-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: trimmed, password })
      });

      let data = {};
      try { data = await response.json(); } catch (_) {}

      if (response.status === 429) {
        const retryAfterMs = data?.retryAfterMs || 5 * 60 * 1000;
        const until = Date.now() + retryAfterMs;
        localStorage.setItem('authCooldownUntil', String(until));
        setCooldownUntil(until);
        setError('Too many attempts. Please wait before retrying.');
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      const { username: resolvedName, accountId, userId, role, sectors, userAuthToken } = data.data || {};

      if (!resolvedName || !accountId) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('userName', resolvedName);
      localStorage.setItem('userAccountId', accountId);

      if (userId)
        localStorage.setItem('userId', userId);

      if (userAuthToken)
        localStorage.setItem('userAuthToken', userAuthToken);

      // Clear any cooldown on success
      localStorage.removeItem('authCooldownUntil');
      setCooldownUntil(0);

      if (typeof onAuthenticated === 'function') {
        onAuthenticated({ label: resolvedName, value: accountId, role, sectors });
        postLoginStatus({ isLoggedIn: true, username: resolvedName });
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-2">Se connecter</h2>
        <p className="text-gray-600 mb-4">Veuillez saisir vos identifiants pour continuer.</p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Nom d'utilisateur ou email"
            className="w-full border rounded-md px-3 py-2 mb-3"
            disabled={isLoading || remainingMs > 0}
            autoFocus
          />
          <div className="relative mb-3">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className="w-full border rounded-md px-3 py-2 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {/* Simple eye / eye-off icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                aria-hidden="true"
              >
                {showPassword ? (
                  <>
                    <path
                      d="M12 5C7 5 3.3 8.1 2 12c1.3 3.9 5 7 10 7s8.7-3.1 10-7c-1.3-3.9-5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M9.5 9.5 14.5 14.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M14.5 9.5 9.5 14.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </>
                ) : (
                  <>
                    <path
                      d="M12 5C7 5 3.3 8.1 2 12c1.3 3.9 5 7 10 7s8.7-3.1 10-7c-1.3-3.9-5-7-10-7z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </>
                )}
              </svg>
            </button>
          </div>

          {error && remainingMs === 0 && (
            <div className="text-red-600 text-sm mb-3">{error}</div>
          )}

          {remainingMs > 0 && (
            <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm mb-3">
              Too many failed attempts. Try again in <strong>{formatMs(remainingMs)}</strong>.
            </div>
          )}

          <button
            type="submit"
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md disabled:opacity-60"
            disabled={isLoading || remainingMs > 0 || !password || !username}
          >
            {isLoading ? 'Vérification…' : 'Valider'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SimpleAuth;
